<!-- src/views/engineering/projects/ProjectDetailView.vue -->
<template>
  <div class="min-h-screen bg-[#F8FAFC] p-4 sm:p-6 md:p-8 relative selection:bg-[#20FCF1]/30 font-sans text-left">
    
    <!-- LOADING STATE -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center py-32 bg-white rounded-[40px] max-w-7xl mx-auto shadow-sm border border-slate-100">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#20FCF1] border-t-transparent mb-6"></div>
      <p class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Decrypting Asset Matrix...</p>
    </div>

    <!-- ERROR STATE -->
    <div v-else-if="!project" class="max-w-7xl mx-auto text-center py-20 bg-rose-50 border border-rose-100 rounded-[40px]">
      <ExclamationTriangleIcon class="h-12 w-12 text-rose-500 mx-auto mb-4" />
      <p class="text-rose-700 font-black uppercase tracking-widest">Project index not found.</p>
      <router-link :to="{ name: 'EngineeringProjects' }" class="mt-6 inline-block text-xs font-black uppercase text-slate-400 hover:text-slate-600 tracking-widest underline">Return to Directory</router-link>
    </div>
    
    <div v-else class="max-w-7xl mx-auto pb-24">
      
      <!-- NAVIGATION & HEADER -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-6">
        <div>
          <router-link :to="{ name: 'EngineeringProjects' }" class="group flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-[#0A1630] transition-colors mb-4">
            <ArrowLeftIcon class="h-3 w-3 group-hover:-translate-x-1 transition-transform" />
            Back to Registry
          </router-link>
          
          <div class="flex flex-wrap items-center gap-4">
            <h1 class="text-3xl sm:text-4xl font-black text-[#0A1630] uppercase tracking-tighter leading-none">{{ project.name }}</h1>
            <span :class="getStatusClass(project.status)" class="px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-black/5 shadow-sm">
              {{ project.status }}
            </span>
          </div>
          
          <div v-if="project.siteAddress" class="flex items-center mt-4 text-slate-500 gap-2">
             <MapPinIcon class="h-4 w-4 text-[#20FCF1]" />
             <span class="text-xs font-bold uppercase tracking-tight">{{ project.siteAddress }}</span>
          </div>
        </div>
        
        <router-link 
            v-if="userStore.isAdmin || userStore.role === 'manager'" 
            :to="{ name: 'EditProject', params: { id: project.id } }"
            class="bg-[#0A1630] text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all flex items-center gap-2 active:scale-95"
        >
            <PencilSquareIcon class="h-4 w-4" />
            Modify Asset
        </router-link>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-10 animate-fadeIn">
        
        <!-- Main Content -->
        <div class="lg:col-span-2 space-y-10">
          
          <!-- Hero Image -->
          <div class="bg-white rounded-[40px] shadow-2xl border border-slate-100 overflow-hidden h-96 relative group">
             <div class="absolute inset-0 bg-[#0A1630]/20 group-hover:bg-transparent transition-colors duration-700"></div>
             <img v-if="project.imageUrl" :src="project.imageUrl" class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" alt="Project Image">
             <div v-else class="w-full h-full flex items-center justify-center bg-slate-50 text-slate-300 flex-col">
                 <WrenchScrewdriverIcon class="h-16 w-16 mb-4 opacity-20" />
                 <span class="text-[10px] font-black uppercase tracking-widest">No Visual Asset Recorded</span>
             </div>
          </div>

          <!-- Scope / Description -->
          <div class="bg-white rounded-[40px] shadow-xl border border-slate-100 p-10">
             <h2 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.3em] mb-8 border-b border-slate-50 pb-6 flex items-center gap-3">
               <DocumentTextIcon class="h-5 w-5 text-[#20FCF1]" /> Scope of Works
             </h2>
             <div class="text-slate-600 text-sm font-medium leading-relaxed whitespace-pre-wrap selection:bg-[#20FCF1]/20">
                 {{ project.description || 'No specialized scope defined for this asset.' }}
             </div>
          </div>

          <!-- Documents List -->
          <div class="bg-white rounded-[40px] shadow-xl border border-slate-100 p-10">
             <div class="flex items-center justify-between mb-10 border-b border-slate-50 pb-6">
               <h2 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.3em] flex items-center gap-3">
                 <ClipboardIcon class="h-5 w-5 text-[#20FCF1]" /> Asset Registry
               </h2>
               <span class="text-[10px] font-black text-slate-400 bg-slate-50 px-4 py-1.5 rounded-full border border-slate-100">{{ project.documents?.length || 0 }} Files Attached</span>
             </div>
             
             <div v-if="project.documents && project.documents.length > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                 <a v-for="(doc, index) in project.documents" :key="index" :href="doc.url" target="_blank" class="flex items-center p-5 border border-slate-100 rounded-[24px] hover:border-[#20FCF1] hover:bg-[#F8FAFC] transition-all group relative overflow-hidden">
                     <div class="h-12 w-12 bg-slate-50 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-[#0A1630] transition-colors">
                        <DocumentIcon class="h-6 w-6 text-slate-400 group-hover:text-[#FD5E00]" />
                     </div>
                     <div class="min-w-0">
                         <p class="text-xs font-black text-[#0A1630] uppercase truncate tracking-tight">{{ doc.name }}</p>
                         <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Secure View &larr;</p>
                     </div>
                 </a>
             </div>
             <div v-else class="text-center py-10 bg-slate-50 rounded-[32px] border border-dashed border-slate-200">
               <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">Zero documentation packets found.</p>
             </div>
          </div>

        </div>

        <!-- Sidebar Info -->
        <div class="space-y-10">
            
            <!-- 🛡️ CLIENT FEEDBACK TACTICAL LINK CARD -->
            <div v-if="userStore.isAdmin || userStore.role === 'manager' || userStore.isManager || userStore.isDirector" class="bg-[#0A1630] rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] p-10 text-white relative overflow-hidden">
                <div class="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                    <StarIcon class="h-28 w-28 text-[#20FCF1]" />
                </div>
                <div class="relative z-10 space-y-4">
                    <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-[0.3em] block">Audit & Closeout</span>
                    <h3 class="text-xl font-black uppercase tracking-tight">Client Feedback Registry</h3>
                    <p class="text-xs text-slate-300 font-medium leading-relaxed">View submitted Client Feedback Questionnaires linked directly to this project.</p>
                    
                    <router-link 
                        :to="{ name: 'AdminEngineeringDashboard', query: { tab: 'clientFeedback', search: project.name } }"
                        class="mt-4 w-full bg-[#20FCF1] text-[#0A1630] py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all flex items-center justify-center gap-2 shadow-lg"
                    >
                        <QrCodeIcon class="h-4 w-4" /> <span>View Project Questionnaires</span>
                    </router-link>
                </div>
            </div>

            <!-- Key Details -->
            <div class="bg-white rounded-[40px] shadow-xl border border-slate-100 p-10">
                <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.3em] mb-8">Metadata Index</h3>
                <dl class="space-y-8">
                    <div class="border-b border-slate-50 pb-4">
                        <dt class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Operation Window</dt>
                        <dd class="text-sm font-black text-[#0A1630] uppercase tracking-tighter">{{ formatDate(project.startDate) }} &mdash; {{ formatDate(project.endDate) }}</dd>
                    </div>
                    <div v-if="project.bridgeRef" class="border-b border-slate-50 pb-4">
                        <dt class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Bridge Identifier</dt>
                        <dd class="text-sm font-black text-[#0A1630] uppercase tracking-tighter">{{ project.bridgeRef }}</dd>
                    </div>
                     <div v-if="project.lineRef" class="border-b border-slate-50 pb-4">
                        <dt class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Track Reference</dt>
                        <dd class="text-sm font-black text-[#0A1630] uppercase tracking-tighter">{{ project.lineRef }}</dd>
                    </div>
                    <div v-if="project.siteWhat3Words" class="bg-[#EBFDF8] p-6 rounded-[24px] border border-[#20FCF1]/20">
                        <dt class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] mb-3">Satellite Coordinates</dt>
                        <dd>
                            <a :href="'https://what3words.com/' + project.siteWhat3Words" target="_blank" class="text-[#0A1630] font-black hover:text-[#FD5E00] transition-colors flex items-center gap-2">
                                <img src="/images/what3words-logo.png" class="h-4 w-auto grayscale brightness-0" alt="w3w">
                                /// {{ project.siteWhat3Words }}
                            </a>
                        </dd>
                    </div>
                </dl>
            </div>

            <!-- Location Map -->
            <div class="bg-white rounded-[40px] shadow-xl border border-slate-100 p-10 overflow-hidden">
                 <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.3em] mb-8">Deployment Map</h3>
                 
                 <div class="relative">
                    <div id="project-map" class="bg-slate-100 h-64 rounded-[32px] overflow-hidden shadow-inner grayscale hover:grayscale-0 transition-all duration-700 border border-slate-200"></div>
                 </div>
                 
                 <a v-if="project.siteAddress" :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(project.siteAddress)" target="_blank" class="block mt-8 text-center w-full bg-[#0A1630] text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] shadow-lg transition-all active:scale-95">
                     External Satellite View
                 </a>
            </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global google */
import { ref, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { db } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import { loadGoogleMaps } from '@/utils/googleMaps';
import { 
  ArrowLeftIcon, MapPinIcon, PencilSquareIcon, 
  WrenchScrewdriverIcon, DocumentTextIcon, ClipboardIcon,
  DocumentIcon, ExclamationTriangleIcon, StarIcon, QrCodeIcon
} from '@heroicons/vue/24/outline';

const route = useRoute();
const userStore = useUserStore();

const project = ref(null);
const isLoading = ref(true);

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBC';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getStatusClass = (status) => {
  const map = { 
    'Active': 'bg-[#EBFDF8] text-[#20FCF1]', 
    'In Planning': 'bg-blue-50 text-blue-600', 
    'Complete': 'bg-slate-100 text-slate-500', 
    'On Hold': 'bg-amber-50 text-amber-600' 
  };
  return map[status] || 'bg-slate-100 text-slate-400';
};

const fetchProject = async () => {
    try {
        if (!userStore.clientId) return;
        const docRef = doc(db, 'clients', userStore.clientId, 'projects', route.params.id);
        const snap = await getDoc(docRef);
        
        if (snap.exists()) {
            project.value = { id: snap.id, ...snap.data() };
            nextTick(() => {
                initMap();
            });
        }
    } catch (err) {
        console.error("Fetch failure:", err);
    } finally {
        isLoading.value = false;
    }
};

const initMap = async () => {
    const address = project.value?.siteAddress || project.value?.location;
    if (!address) return;

    try {
        await loadGoogleMaps();
        const mapDiv = document.getElementById("project-map");
        if (!mapDiv) return;

        const { Map } = await google.maps.importLibrary("maps");
        const { Geocoder } = await google.maps.importLibrary("geocoding");
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

        const map = new Map(mapDiv, {
            center: { lat: 54.5, lng: -4.0 },
            zoom: 14,
            disableDefaultUI: true,
            zoomControl: true,
            mapId: "4504f8b37365c3d0" 
        });

        const g = new Geocoder();
        g.geocode({ address: address }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const position = results[0].geometry.location;
                map.setCenter(position);
                new AdvancedMarkerElement({
                    map: map,
                    position: position,
                    title: project.value.name
                });
            }
        });
    } catch (err) {
        console.error("Map initialization failed:", err);
    }
};

onMounted(() => {
    if (userStore.isAuthReady) fetchProject();
    else {
        const unwatch = userStore.$subscribe(() => {
            if (userStore.isAuthReady) {
                fetchProject();
                unwatch();
            }
        });
    }
});
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

/* Custom Map Scrollbar override */
:deep(.gm-style) { border-radius: 32px; overflow: hidden; }
</style>